import React, { useRef, useEffect, useState, useMemo } from 'react';
import Globe from 'react-globe.gl';
import { motion, AnimatePresence } from 'framer-motion';
import { AGENCIES } from '../mock';
import { fetchDossiers, geoForLocation, imageUrl } from '../api';
import { Link } from 'react-router-dom';
import { MapPin, ArrowUpRight, RotateCcw, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';

export default function Globe3D() {
  const globeEl = useRef();
  const containerRef = useRef(null);
  const closeTimer = useRef(null);
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(false);
  const [activeAgency, setActiveAgency] = useState('ALL');
  const [dims, setDims] = useState({ w: 800, h: 480 });
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { items } = await fetchDossiers({ limit: 200 });
        if (!mounted) return;
        setDossiers(items);
      } catch (e) {
        console.error('fetch dossiers globe', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!globeEl.current) return;
    const controls = globeEl.current.controls();
    controls.autoRotate = false;
    controls.enableZoom = true;
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.minDistance = 180;
    controls.maxDistance = 600;
    controls.rotateSpeed = 0.8;
    globeEl.current.pointOfView({ lat: 25, lng: 10, altitude: 2.4 }, 0);
  }, []);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const r = containerRef.current.getBoundingClientRect();
        setDims({ w: r.width, h: Math.max(440, r.height) });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Convert dossiers to geo points using location field
  const points = useMemo(() => {
    const pts = [];
    for (const d of dossiers) {
      if (activeAgency !== 'ALL' && d.agency !== activeAgency) continue;
      const geo = geoForLocation(d.location);
      if (!geo) continue;
      pts.push({
        ...d,
        lat: geo[0] + (Math.random() - 0.5) * 1.4, // slight jitter to avoid overlap
        lng: geo[1] + (Math.random() - 0.5) * 1.4,
        color: AGENCIES.find(a => a.id === d.agency)?.color || '#22d3ee',
      });
    }
    return pts;
  }, [dossiers, activeAgency]);

  const rings = useMemo(() => points.map(p => ({ lat: p.lat, lng: p.lng, color: p.color })), [points]);

  const arcs = useMemo(() => {
    const out = [];
    for (let i = 0; i < Math.min(points.length, 18); i++) {
      const a = points[i];
      const b = points[(i + 5) % points.length];
      if (!b) continue;
      out.push({ startLat: a.lat, startLng: a.lng, endLat: b.lat, endLng: b.lng, color: [a.color, b.color] });
    }
    return out;
  }, [points]);

  // Cancel close timer (used when user moves cursor onto popup)
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      if (!pinned) setHover(null);
    }, 700);
  };

  // HTML custom markers with delayed-close hover
  const htmlElements = useMemo(() => points.map(p => ({ ...p, htmlElement: createMarker(p) })), [points]);

  function createMarker(p) {
    const el = document.createElement('div');
    el.className = 'globe-marker';
    el.style.setProperty('--c', p.color);
    el.innerHTML = `
      <div class="gm-outer">
        <div class="gm-ring"></div>
        <div class="gm-ring gm-ring-2"></div>
        <div class="gm-core"></div>
      </div>
      <div class="gm-label mono">${p.id.slice(0, 18)}</div>
    `;
    el.style.cursor = 'pointer';
    el.onmouseenter = () => { cancelClose(); setHover(p); };
    el.onmouseleave = () => scheduleClose();
    el.onclick = (e) => {
      e.stopPropagation();
      setHover(p);
      setPinned(true);
      focusOn(p.lat, p.lng);
    };
    return el;
  }

  const focusOn = (lat, lng) => {
    if (globeEl.current) globeEl.current.pointOfView({ lat, lng, altitude: 1.4 }, 1200);
  };
  const resetView = () => globeEl.current?.pointOfView({ lat: 25, lng: 10, altitude: 2.4 }, 1000);
  const zoom = (factor) => {
    if (!globeEl.current) return;
    const pov = globeEl.current.pointOfView();
    globeEl.current.pointOfView({ ...pov, altitude: Math.max(0.6, Math.min(4, pov.altitude * factor)) }, 500);
  };

  return (
    <div ref={containerRef} className="relative w-full h-[520px] rounded-xl overflow-hidden glass globe-glow" onClick={() => { setPinned(false); setHover(null); }}>
      <div className="absolute inset-0 stars-bg twinkle pointer-events-none" />
      <Globe
        ref={globeEl}
        width={dims.w}
        height={dims.h}
        backgroundColor="rgba(0,0,0,0)"
        globeImageUrl="https://unpkg.com/three-globe/example/img/earth-night.jpg"
        bumpImageUrl="https://unpkg.com/three-globe/example/img/earth-topology.png"
        atmosphereColor="#22d3ee"
        atmosphereAltitude={0.18}
        showAtmosphere={true}
        htmlElementsData={htmlElements}
        htmlLat="lat"
        htmlLng="lng"
        htmlAltitude={0.012}
        htmlElement={(d) => d.htmlElement}
        ringsData={rings}
        ringLat="lat"
        ringLng="lng"
        ringColor={(d) => (t) => `${d.color}${Math.floor((1 - t) * 200).toString(16).padStart(2, '0')}`}
        ringMaxRadius={2.8}
        ringPropagationSpeed={1.6}
        ringRepeatPeriod={1800}
        arcsData={arcs}
        arcStartLat="startLat"
        arcStartLng="startLng"
        arcEndLat="endLat"
        arcEndLng="endLng"
        arcColor="color"
        arcDashLength={0.4}
        arcDashGap={2.2}
        arcDashAnimateTime={3500}
        arcStroke={0.3}
      />

      <div className="absolute top-3 left-3 glass-strong rounded-md px-2 py-1.5 z-10 flex items-center gap-1.5 flex-wrap" onClick={e => e.stopPropagation()}>
        <span className="mono text-[9px] tracking-widest text-3 uppercase mr-1">Agence</span>
        <button onClick={() => setActiveAgency('ALL')} className={`mono text-[10px] px-1.5 py-0.5 rounded ${activeAgency==='ALL' ? 'bg-white/15 text-1' : 'text-2 hover:bg-white/5'}`}>Toutes <span className="text-3">{dossiers.length}</span></button>
        {AGENCIES.map(a => (
          <button key={a.id} onClick={() => setActiveAgency(a.id)}
                  className={`mono text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${activeAgency===a.id ? 'bg-white/15 text-1' : 'text-2 hover:bg-white/5'}`}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: a.color }} />
            {a.short} <span className="text-3">{dossiers.filter(d => d.agency === a.id).length}</span>
          </button>
        ))}
      </div>

      <div className="absolute top-3 right-3 glass-strong rounded-md px-2.5 py-1.5 z-10 flex items-center gap-3 mono text-[10px]" onClick={e => e.stopPropagation()}>
        <span className="text-3">TOTAL <span className="text-1">{dossiers.length}</span></span>
        <span className="text-3">GÉO <span className="text-1">{points.length}</span></span>
        {loading && <Loader2 className="h-3 w-3 animate-spin text-cyan-300" />}
      </div>

      <div className="absolute bottom-3 right-3 flex gap-1.5 z-10" onClick={e => e.stopPropagation()}>
        <button onClick={() => zoom(0.7)} className="btn-ghost p-1.5 rounded-md" title="Zoomer"><ZoomIn className="h-3.5 w-3.5" /></button>
        <button onClick={() => zoom(1.4)} className="btn-ghost p-1.5 rounded-md" title="Dézoomer"><ZoomOut className="h-3.5 w-3.5" /></button>
        <button onClick={resetView} className="btn-ghost p-1.5 rounded-md" title="Réinitialiser"><RotateCcw className="h-3.5 w-3.5" /></button>
      </div>

      <AnimatePresence>
        {hover && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            className="absolute bottom-3 left-3 md:max-w-md glass-strong rounded-md p-3 z-20 pointer-events-auto"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onClick={e => { e.stopPropagation(); cancelClose(); setPinned(true); }}
            style={{ paddingBottom: 14 }}
          >
            {/* invisible bridge area to bridge cursor gap between marker and popup */}
            <div style={{ position: 'absolute', inset: '-18px -18px 0 -18px', pointerEvents: 'auto' }} onMouseEnter={cancelClose} />
            {hover.image_url && (
              <img src={imageUrl(hover.image_url)} alt={hover.title} className="w-full h-24 object-cover rounded mb-2 relative" />
            )}
            <div className="flex items-center gap-2 mb-1.5 relative">
              <span className="badge-classif" style={{ color: hover.color }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: hover.color }} /> {AGENCIES.find(a=>a.id===hover.agency)?.short}
              </span>
              <span className="mono text-[10px] text-3 truncate">{hover.id}</span>
              {pinned && (
                <button onClick={e => { e.stopPropagation(); setPinned(false); setHover(null); }} className="ml-auto text-3 hover:text-1 text-xs">✕</button>
              )}
            </div>
            <div className="text-1 text-sm font-medium leading-snug mb-1 relative">{hover.title}</div>
            {hover.summary && <p className="text-3 text-[11px] leading-relaxed mb-2 line-clamp-2 relative">{hover.summary}</p>}
            <div className="flex items-center justify-between gap-3 mt-1 relative">
              <span className="inline-flex items-center gap-1 text-[11px] text-3"><MapPin className="h-3 w-3" /> {hover.location}</span>
              <Link to={`/dossier/${hover.id}`} onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 text-cyan-300 text-xs hover:text-cyan-200 px-2 py-1 rounded border border-cyan-300/30 hover:bg-cyan-400/10">
                Ouvrir <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
